import { BASE_API_URL } from 'utils/constants';
import { deleted, downloadFile, downloadFilePost, get, post, put } from '../../utils/api';
import { DataResponse } from 'services/response';
import { TransitionStatus, TransitionButtonModel } from 'models/PurchaseOrder';
import { SearchHistory } from './purchaseOrder_services';
import { HistoryEditModel } from 'models/DeliveryRegistration';

export interface WasteHandoverPlanSearchPayload {
  paging: {
    pageSize: number;
    pageIndex: number;
    orderBy?: string;
    orderByDesc?: string;
  };
  saleOrgCode?: string;
  wasteGroupingEnvironmentPermitId?: string;
  wasteGroupingContractIds?: string[];
  wasteHandoverPlanCode?: string;
  productCode?: string;
  productName?: string;
  vendorNumber?: string;
  vendorName?: string;
  storageLocation?: string;
  handoverDocumentNumber?: string;
  deliveryDateFrom?: string;
  deliveryDateTo?: string;
  createTimeFrom?: string;
  createTimeTo?: string;
  createByIds?: string[];
  actived?: boolean;
}

export interface WasteHandoverPlanItem {
  deliveryDate: string;
  wasteGroupCode?: string;
  productCode: string;
  productName: string;
  vendorNumber: string;
  vendorName: string;
  storageLocation: string;
  handoverDocumentNumber: string;
  note?: string;
}

export interface WasteHandoverPlanCreateRequest {
  items: WasteHandoverPlanItem[];
}

export interface WasteHandoverPlanUpdateRequest {
  wasteHandoverPlanId: string;
  deliveryDate: string;
  wasteGroupCode?: string;
  productCode: string;
  productName: string;
  vendorNumber: string;
  vendorName: string;
  storageLocation: string;
  handoverDocumentNumber: string;
  note?: string;
  actived: boolean;
}

export interface WasteHandoverPlanModel {
  productName: any;
  stt: number;
  wasteHandoverPlanId: string;
  deliveryDate: string;
  wasteHandoverPlanCode: string;
  wasteGroupingEnvironmentPermitName?: string;
  wasteGroupingContractName?: string;
  wasteGroup?: string;
  productCode: string;
  materialName?: string;
  vendorNumber: string;
  vendorName: string;
  storageLocation: string;
  storageLocationName?: string;
  handoverDocumentNumber: string;
  saleOrgCode: string;
  saleOrgName?: string;
  note?: string;
  actived: boolean;
  createTime: string;
  createByName?: string;
  lastEditTime?: string | null;
  lastEditByName?: string | null;
  productType?: string;
  isRegistered?: boolean;
  isChoCanRa?: boolean; // Flag để xác định item có được cân ra không
  isSentEmail?: boolean; // Flag để xác định item đã được gửi email
}

export type WasteHandoverPlanExportPayload = Omit<WasteHandoverPlanSearchPayload, 'paging'>;

export interface WasteHandoverPlanSendEmailPayload extends WasteHandoverPlanExportPayload {
  plantCode?: string;
}

// Interface cho search phiếu đăng ký bàn giao chất thải/phế liệu
export interface WasteHandoverRegistrationSearchPayload {
  paging: {
    pageSize: number;
    pageIndex: number;
    orderBy?: string;
    orderByDesc?: string;
  };
  vendorNumber?: string;
  vendorName?: string;
  wasteHandoverRegistrationCode?: string;
  driverName?: string;
  documentDateFrom?: string;
  documentDateTo?: string;
  productCode?: string;
  productName?: string;
  taskStatusCode?: string;
}

// Interface cho tạo phiếu đăng ký bàn giao chất thải/phế liệu
export interface DeliveryTimeItem {
  deliveryTime: string;
  deliveryQuantity: number;
  wasteHandoverRegistrationTimeId?: string;
  isDelivered?: boolean;
}

export interface DeliveryInfoItem {
  wasteHandoverPlanId: string;
  wasteHandoverPlanCode: string;
  productCode: string;
  productName: string;
  plannedQuantity: number;
  deliveredQuantity: number;
  openQuantity: number;
  storageLocation: string;
  storageLocationName?: string;
  deliveryStatus?: string;
  deliveryStatusName?: string;
}

export interface WasteHandoverRegistrationCreateRequest {
  vendorNumber: string;
  deliveryTimeItems: DeliveryTimeItem[];
  driverName?: string;
  phone?: string;
  licensePlate?: string;
  licenseNumber?: string;
  result?: string;
  companionInfo?: string;
  deliveryInfoItems: DeliveryInfoItem[];
  isSendEmail: boolean;
  periodTime: string;
  isTickCheckBox: boolean;
  isTime: boolean;
}

export interface WasteHandoverRegistrationUpdateRequest {
  wasteHandoverRegistrationId: string;
  vendorNumber: string;
  deliveryTimeItems: DeliveryTimeItem[];
  driverName?: string;
  phone?: string;
  licensePlate?: string;
  licenseNumber?: string;
  result?: string;
  companionInfo?: string;
  isSendEmail: boolean;
  deliveryInfoItems: {
    stt: number;
    wasteHandoverPlanId: string;
    wasteHandoverPlanCode: string;
    productCode: string;
    productName: string;
    plannedQuantity: number;
    deliveredQuantity: number;
    openQuantity: number;
    storageLocation: string;
    storageLocationName?: string;
    deliveryStatus?: string;
    deliveryStatusName?: string;
    wasteGroupingContractId?: string | null;
    wasteGroupingContractName?: string;
    firstWeight?: number | null;
    secondWeight?: number | null;
    actualDeliveryWeight?: number | null;
    unit?: string;
    note?: string;
    noPacking?: number;
    perPackingWeight?: number;
    packingWeight?: number;
  }[];
  periodTime?: string;
  isTickCheckBox: boolean;
  isTime: boolean;
}

export interface IWasteHandoverPlanServices {
  searchWasteHandoverPlans(data: WasteHandoverPlanSearchPayload): Promise<DataResponse<WasteHandoverPlanModel[]>>;
  createWasteHandoverPlan(data: WasteHandoverPlanCreateRequest): Promise<DataResponse<any>>;
  updateWasteHandoverPlan(data: WasteHandoverPlanUpdateRequest): Promise<DataResponse<any>>;
  getWasteHandoverPlanDetail(id: string): Promise<DataResponse<WasteHandoverPlanModel>>;
  exportWasteHandoverPlanList(data: WasteHandoverPlanExportPayload): Promise<void>;
  exportWasteHandoverPlanCreateTemplate(): Promise<void>;
  exportWasteHandoverPlanEditTemplate(data: WasteHandoverPlanExportPayload): Promise<void>;
  sendEmail(data: WasteHandoverPlanSendEmailPayload): Promise<DataResponse<any>>;
  deleteWasteHandoverPlan(id: string): Promise<DataResponse<any>>;
  createWasteHandoverRegistration(data: WasteHandoverRegistrationCreateRequest): Promise<DataResponse<any>>;
  updateWasteHandoverRegistration(data: WasteHandoverRegistrationUpdateRequest): Promise<DataResponse<any>>;
  searchWasteHandoverRegistration(data: WasteHandoverRegistrationSearchPayload): Promise<DataResponse<any[]>>;
  getDetailWasteHandoverRegistration(id: string): Promise<DataResponse<any>>;
  cancelWasteHandoverRegistration(data: { cancelReason: string; wasteHandoverRegistrationId: string }): Promise<DataResponse<null>>;
  getStatusTranslation(taskId: string): Promise<DataResponse<TransitionStatus[]>>;
  getTransitionButton(taskId: string): Promise<DataResponse<TransitionButtonModel[]>>;
  executeTransitionButton(data: {
    taskId: string;
    statusTransitionId: string;
    comment?: string;
    files?: string[];
    documentNumber?: string;
    deliveryStatuses?: Array<{
      wasteHandoverRegistrationDetailId: string;
      deliveryStatus: string;
    }>;
    numberOfTare?: number;
    tareWeight?: number;
    tareDeduction?: number;
  }): Promise<DataResponse<null>>;
  getTimeline(taskId: string): Promise<DataResponse<any[]>>;

  // Lịch sử chỉnh sửa
  getHistory(data: SearchHistory): Promise<DataResponse<HistoryEditModel[]>>

  importExcel(data: any): Promise<DataResponse<any>>;
}

export function createWasteHandoverPlanServices(): IWasteHandoverPlanServices {
  return {
    searchWasteHandoverPlans: async (data) => {
      return (await post(BASE_API_URL, `wastehandoverplan/search`, data)).data;
    },
    createWasteHandoverPlan: async (data) => {
      return (await post(BASE_API_URL, `wastehandoverplan/create`, data)).data;
    },
    updateWasteHandoverPlan: async (data) => {
      return (await put(BASE_API_URL, `wastehandoverplan/update`, data)).data;
    },
    getWasteHandoverPlanDetail: async (id) => {
      return (await get(BASE_API_URL, `wastehandoverplan/${id}`)).data;
    },
    exportWasteHandoverPlanList: async (data) => {
      await downloadFilePost(BASE_API_URL, `wastehandoverplan/export-list`, data);
    },
    exportWasteHandoverPlanCreateTemplate: async () => {
      await downloadFile(`wastehandoverplan/export-create-template`);
    },
    exportWasteHandoverPlanEditTemplate: async (data) => {
      await downloadFilePost(BASE_API_URL, `wastehandoverplan/export-edit-template`, data);
    },
    sendEmail: async (data) => {
      return (await post(BASE_API_URL, `wastehandoverplan/send-email`, data)).data;
    },
    deleteWasteHandoverPlan: async (id) => {
      return (await deleted(BASE_API_URL, `wastehandoverplan/${id}`)).data;
    },
    createWasteHandoverRegistration: async (data) => {
      return (await post(BASE_API_URL, `wastehandoverregistration/create`, data)).data;
    },
    updateWasteHandoverRegistration: async (data) => {
      return (await put(BASE_API_URL, `wastehandoverregistration/update`, data)).data;
    },
    searchWasteHandoverRegistration: async (data) => {
      return (await post(BASE_API_URL, `wastehandoverregistration/search`, data)).data;
    },
    getDetailWasteHandoverRegistration: async (id) => {
      return (await get(BASE_API_URL, `wastehandoverregistration/${id}`)).data;
    },
    cancelWasteHandoverRegistration: async (data) => {
      return (await post(BASE_API_URL, `wastehandoverregistration/cancel`, data)).data;
    },
    getStatusTranslation: async (taskId) => {
      return (await post(BASE_API_URL, `taskstatus/get-status-transition?TaskId=${taskId}`, {})).data;
    },
    getTransitionButton: async (taskId) => {
      return (await post(BASE_API_URL, `taskstatus/get-transition-button`, { taskId })).data;
    },
    executeTransitionButton: async (data) => {
      return (await post(BASE_API_URL, `taskstatus/execute-transition-button`, data)).data;
    },
    getTimeline: async (taskId) => {
      return (await get(BASE_API_URL, `taskstatus/get-timeline?TaskId=${taskId}`)).data;
    },

    // Lịch sử chỉnh sửa
    getHistory: async (data) => {
      return (await post(BASE_API_URL, `wastehandoverregistration/get-history`, data)).data;
    },
    importExcel: async (data) => {
      return (await post(BASE_API_URL, `wastehandoverplan/import`, data)).data;
    },
  };
}

